import { Root } from "mdast";
import { Plugin } from "unified";
import { visit } from "unist-util-visit";
import { createCustomElement } from "../react-markdown-registry/utils";

import { RemarkCitationGroupProps, CitationMetadata } from "./types";

/**
 * Remark plugin that groups adjacent paper_cite nodes into citationGroup nodes
 * This runs after remarkLegacyNumberCitations has converted [1] to paper_cite nodes
 *
 * Looks for patterns like:
 * - paper_cite paper_cite paper_cite (adjacent paper_cite nodes) -> citationGroup
 * - paper_cite text paper_cite (non-adjacent) -> separate citationGroups
 *
 * Preserves all metadata from individual paper_cite nodes and adds grouping context
 */
export const remarkCitationGrouping: Plugin<[], Root> = () => {
  let globalCitationCounter = 0;
  let citedSentences: Record<string, number> = {};
  let sentenceCounter = 0;

  return (tree) => {
    // Find all paper_cite nodes and group adjacent ones
    visit(tree, (node: any, index, parent: any) => {
      if (!parent?.children || index === undefined) return;

      // Look for paper_cite nodes and check if they should be grouped
      if (node.type === "paper_cite") {
        // Skip if this node was already processed by grouping
        if ((node as any).__remarkCitationGroupProcessed) return;
        const siblings = parent.children;
        let groupStart = index;
        let groupEnd = index;

        // Look backwards for the start of the group
        // Include both adjacent paper_cite nodes and those separated by single spaces
        while (groupStart > 0) {
          const prevNode: any = siblings[groupStart - 1];
          if (prevNode.type === "paper_cite") {
            // Direct adjacency - always group
            groupStart--;
          } else if (
            prevNode.type === "text" &&
            prevNode.value === " " // Only single space
          ) {
            // Single space - check if there's a paper_cite before it
            if (
              groupStart > 1 &&
              siblings[groupStart - 2].type === "paper_cite"
            ) {
              groupStart -= 2; // Skip both space and citation
            } else {
              break;
            }
          } else {
            break;
          }
        }

        // Look forwards for the end of the group
        // Include both adjacent paper_cite nodes and those separated by single spaces
        while (groupEnd < siblings.length - 1) {
          const nextNode: any = siblings[groupEnd + 1];
          if (nextNode.type === "paper_cite") {
            // Direct adjacency - always group
            groupEnd++;
          } else if (
            nextNode.type === "text" &&
            nextNode.value === " " // Only single space
          ) {
            // Single space - check if there's a paper_cite after it
            if (
              groupEnd < siblings.length - 2 &&
              siblings[groupEnd + 2].type === "paper_cite"
            ) {
              groupEnd += 2; // Skip both space and citation
            } else {
              break;
            }
          } else {
            break;
          }
        }

        // Only process if we're at the start of a group and the group has multiple citations
        if (index === groupStart && groupEnd > groupStart) {
          const citesInGroup = siblings
            .slice(groupStart, groupEnd + 1)
            .filter((n: any) => n.type === "paper_cite") as any[];

          if (citesInGroup.length > 1) {
            // Extract full citation metadata
            const citations: CitationMetadata[] = citesInGroup.map(
              (cite: any) => {
                const props = cite.data?.hProperties || {};
                return {
                  paperIndex: props.paperIndex,
                  paper_id: props.paper_id,
                  quote: props.quote,
                  quoteIndex: props.quoteIndex,
                };
              }
            );

            // Use metadata from the first cite node for the group
            const firstCite = citesInGroup[0];
            const citeProps = firstCite.data?.hProperties || {};

            // Handle sentence tracking
            let sentenceNumber = 0;
            const sentenceText = citeProps.associatedSentence || "";
            if (sentenceText) {
              if (citedSentences[sentenceText]) {
                sentenceNumber = citedSentences[sentenceText];
              } else {
                sentenceNumber = ++sentenceCounter;
                citedSentences[sentenceText] = sentenceNumber;
              }
            }

            // Check for following punctuation by looking at the next sibling
            let followingPunctuation = "";
            if (groupEnd + 1 < siblings.length) {
              const nextSibling: any = siblings[groupEnd + 1];
              if (nextSibling.type === "text" && nextSibling.value) {
                const punctuationMatch = nextSibling.value.match(/^([^\w\s]+)/);
                if (punctuationMatch) {
                  followingPunctuation = punctuationMatch[1];
                  // Remove the punctuation from the text node
                  nextSibling.value = nextSibling.value.substring(
                    followingPunctuation.length
                  );
                }
              }
            }

            const citationGroupProps: RemarkCitationGroupProps = {
              citations: JSON.stringify(citations),
              referenceLineNumber: sentenceNumber,
              associatedSentence: sentenceText,
              stableKey: `citation-group-${++globalCitationCounter}`,
              followingPunctuation,
              isTableCitation: citeProps.isTableCitation || false,
            };

            // Create the citation group node
            const citationGroupNode = createCustomElement(
              "citationGroup",
              citationGroupProps
            );

            // Mark all nodes in the group as processed before replacing
            for (let i = groupStart; i <= groupEnd; i++) {
              const nodeToMark = siblings[i];
              if (nodeToMark && nodeToMark.type === "paper_cite") {
                (nodeToMark as any).__remarkCitationGroupProcessed = true;
              }
            }

            // Replace all nodes in the group with the single citationGroup node
            (parent as any).children.splice(
              groupStart,
              groupEnd - groupStart + 1,
              citationGroupNode
            );

            // Mark the new node as processed to avoid re-processing
            (citationGroupNode as any).__remarkCitationGroupProcessed = true;
          }
        }
      }
    });
  };
};
